import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { scrollTo as scrollPageTo } from '@theme/scroll-container';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Qualux speed 2026-10-10: alleen het eerste galerijbeeld laadt direct, de
    // rest is loading="lazy". In een horizontale carrousel kan een lazy beeld
    // bij het swipen even leeg zijn, omdat de carrousel het afknipt en de
    // browser het dan pas laat ophalen. Zodra iemand de galerij aanraakt, halen
    // we de rest alsnog direct op: de vinger raakt het scherm voordat de swipe
    // klaar is, dus het volgende beeld is dan al onderweg. Eenmalig per galerij.
    // De zoom-dialog staat binnen dit element en bevat dezelfde beelden op 3840 px;
    // die laten we bewust lazy, anders haalt een aanraking ze allemaal binnen.
    const upgrade = () => {
      for (const img of this.querySelectorAll('img.product-media__image[loading="lazy"]')) {
        if (!img.closest('zoom-dialog')) img.loading = 'eager';
      }
    };
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin']) {
      this.addEventListener(type, upgrade, { once: true, passive: true, signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const previousColor = this.dataset.colorFiltered;
        this.replaceWith(newMediaGallery);

        // Qualux: when the selected color changes, scroll the page back to the
        // very top (announcement bar and header included), so the shopper sees
        // that color's photos from the start. 2026-10-12: was scrollIntoView on
        // the gallery, which stopped just below the announcement bar.
        const newColor = newMediaGallery.dataset.colorFiltered;
        if (newColor && newColor !== previousColor) {
          scrollPageTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
